import React from "react";
import { X, Check } from "lucide-react";

const WITHOUT_AI = [
  "Customers leave with unanswered questions",
  "Leads go cold after business hours",
  "Your competitors answer first",
  "You're paying for ads to send people to a dead end",
  "Every missed call could be a $10K–$60K client",
];

const WITH_AI = [
  "Every question gets answered instantly",
  "Leads captured and booked at 3 AM",
  "You close before competitors even respond",
  "Your website actually earns back your ad spend",
  "Zero missed opportunities. Ever.",
];

export default function Urgency() {
  return (
    <section data-testid="urgency-section" className="bg-white fs-section border-y border-black/5">
      <div className="fs-container">
        <div className="max-w-3xl">
          <div className="flex items-center gap-3 mb-5">
            <span className="h-px w-8 bg-[#C9A84C]" />
            <span className="fs-tag">The Competitive Reality</span>
          </div>
          <h2 className="font-display text-4xl md:text-6xl uppercase tracking-[0.03em] leading-[0.95] text-[#0A0F1E]">
            Your Competition Is<br />Already Doing This.
          </h2>
          <p className="mt-6 text-lg text-[#0F172A]/70 max-w-2xl leading-relaxed">
            Enterprise companies and franchises aren't waiting. They've already
            deployed AI agents on their websites. Small businesses are the last
            to move — and that window is closing.
          </p>
        </div>

        <div className="mt-14 grid md:grid-cols-2 gap-6 lg:gap-8">
          {/* Without */}
          <div
            data-testid="urgency-without"
            className="rounded-2xl border border-[#0A0F1E]/10 bg-white p-8 md:p-10"
          >
            <div className="text-[0.7rem] uppercase tracking-[0.22em] text-[#0F172A]/55 font-semibold mb-4">
              Without an AI Agent
            </div>
            <ul className="space-y-4">
              {WITHOUT_AI.map((t, i) => (
                <li key={i} className="flex items-start gap-3 text-[#0F172A]/55">
                  <span className="mt-0.5 h-5 w-5 rounded-full border border-[#0A0F1E]/20 bg-[#0A0F1E]/5 flex items-center justify-center text-[#0F172A]/50 shrink-0">
                    <X size={13} strokeWidth={2.5} />
                  </span>
                  <span className="text-sm md:text-[0.95rem] leading-relaxed">{t}</span>
                </li>
              ))}
            </ul>
          </div>

          {/* With */}
          <div
            data-testid="urgency-with"
            className="rounded-2xl border border-[#C9A84C]/50 bg-[#0A0F1E] text-white p-8 md:p-10 shadow-[0_30px_80px_-40px_rgba(10,15,30,0.45)]"
          >
            <div className="text-[0.7rem] uppercase tracking-[0.22em] text-[#C9A84C] font-semibold mb-4">
              With Always On Sales
            </div>
            <ul className="space-y-4">
              {WITH_AI.map((t, i) => (
                <li key={i} className="flex items-start gap-3 text-white">
                  <span className="mt-0.5 h-5 w-5 rounded-full bg-[#C9A84C] flex items-center justify-center text-[#0A0F1E] shrink-0">
                    <Check size={13} strokeWidth={2.5} />
                  </span>
                  <span className="text-sm md:text-[0.95rem] leading-relaxed">{t}</span>
                </li>
              ))}
            </ul>
          </div>
        </div>

        <p className="mt-12 max-w-3xl font-display text-2xl md:text-3xl uppercase tracking-[0.03em] leading-snug text-[#0A0F1E]">
          Every day your website goes unanswered,{" "}
          <span className="text-[#C9A84C]">a competitor wins the customer you already earned.</span>
        </p>
      </div>
    </section>
  );
}
